<script setup>
const { params, path } = useRoute()
const slug = params.slug
const { locale } = useI18n()

const url = slug && slug.length > 0 ? slug.join('/') : ''

const story = await useAsyncStoryblok(url.replace(/\/$/, ''), {
  version: 'publish',
  language: locale.value,
  resolve_relations: ['body.image-card.references'],
  // cv,
})

if (!story.value)
  throw createError({ statusCode: 404, statusMessage: 'Page Not Found' })

// Index stories come back as "learn-more/" (or ".../index"); schema logic wants the public path.
const pageSlug = (story.value.full_slug || '').replace(/\/index$/, '').replace(/\/$/, '')

const { fetchJobLocation, fetchJobTime, fetchJobCategories, formattedJobLocation, formattedJobTime, formattedJobCategories } = useJobCategories()

await Promise.all([
  fetchJobLocation(),
  fetchJobTime(),
  fetchJobCategories(),
])

function filteredJobLocation() {
  for (let i = 0; i < formattedJobLocation.value.length; i++) {
    const item = formattedJobLocation.value[i]
    if (item.uuid === story.value.content.job_location)
      return item.name
  }
}

function filteredJobTime() {
  for (let i = 0; i < formattedJobTime.value.length; i++) {
    const item = formattedJobTime.value[i]
    if (item.uuid === story.value.content.job_time)
      return item.name
  }
}

function filteredJobCategories() {
  for (let i = 0; i < formattedJobCategories.value.length; i++) {
    const item = formattedJobCategories.value[i]
    if (item.uuid === story.value.content.job_categories)
      return item.name
  }
}

const target = ref(null)
function scrollToElement() {
  target.value.scrollIntoView({ behavior: 'smooth' })
}

const multipleMail = computed(() => {
  const mail = story.value?.content?.multipleMail || story.value?.content?.multiMail || story.value?.content?.mulipleMail

  if (Array.isArray(mail))
    return mail

  return mail ? [mail] : []
})

// News articles use the dedicated "news-article" Storyblok component; case studies share the
// generic "page" component with every other CMS page here, so they're only distinguishable by
// their full_slug prefix. Everything else served by this catch-all (industries, service subpages,
// career postings, etc.) gets no article-type schema — only the sitewide Organization one from app.vue.
function articleSchema() {
  const content = story.value?.content
  if (!content)
    return null

  const isNews = content.component === 'news-article'
  // Case studies and Learn More articles share the generic "page" component too.
  const isCaseStudy = ['case-studies/', 'learn-more/'].some(prefix => pageSlug.startsWith(prefix))
  if (!isNews && !isCaseStudy)
    return null

  return {
    '@context': 'https://schema.org',
    '@type': isNews ? 'NewsArticle' : 'Article',
    'headline': content.title,
    'description': content.description,
    'image': isNews ? content.image?.filename : content.thumbnail?.filename,
    'datePublished': story.value.first_published_at,
    'dateModified': story.value.updated_at,
    'url': `https://www.dexon-technology.com/${story.value.full_slug}`,
    'author': {
      '@type': 'Organization',
      'name': 'Dexon Technology',
    },
    'publisher': {
      '@type': 'Organization',
      'name': 'Dexon Technology',
      'logo': {
        '@type': 'ImageObject',
        'url': 'https://www.dexon-technology.com/logo.webp',
      },
    },
  }
}

// job-location category names are plain office-country names in Storyblok, not ISO codes — map the
// ones that actually exist there (checked via the CDN API) so jobLocation carries a real addressCountry
// instead of just a locality string. Any future country added to that category list falls back to
// addressLocality-only until it's added here.
const JOB_COUNTRY_CODES = {
  'Thailand': 'TH',
  'The Netherlands': 'NL',
  'Singapore': 'SG',
  'Germany': 'DE',
  'Indonesia': 'ID',
  'Argentina': 'AR',
  'United States': 'US',
  'Philippines': 'PH',
}

// "Full Time" / "Part Time" / "Flexible Time" are the actual job-time category labels in Storyblok —
// Google's JobPosting guideline wants one of its EmploymentType enum values; "Flexible Time" has no
// clean match so it falls back to OTHER rather than guessing.
function employmentTypeCode(label) {
  return { 'Full Time': 'FULL_TIME', 'Part Time': 'PART_TIME' }[label] || 'OTHER'
}

function richTextToPlain(doc) {
  const parts = []
  function walk(node) {
    if (node?.text)
      parts.push(node.text)
    node?.content?.forEach(walk)
  }
  doc?.content?.forEach(walk)
  return parts.join(' ').trim()
}

function jobPostingSchema() {
  const content = story.value?.content
  if (content?.component !== 'job-position')
    return null

  const description = [richTextToPlain(content.responsibility), richTextToPlain(content.qualification)]
    .filter(Boolean)
    .join(' ')
  const locationName = filteredJobLocation()

  return {
    '@context': 'https://schema.org',
    '@type': 'JobPosting',
    'title': content.title,
    'description': description || content.title,
    'datePosted': story.value.first_published_at,
    'employmentType': employmentTypeCode(filteredJobTime()),
    'hiringOrganization': {
      '@type': 'Organization',
      'name': 'Dexon Technology',
      'logo': 'https://www.dexon-technology.com/logo.webp',
    },
    'jobLocation': {
      '@type': 'Place',
      'address': {
        '@type': 'PostalAddress',
        'addressLocality': locationName,
        'addressCountry': JOB_COUNTRY_CODES[locationName],
      },
    },
    'url': `https://www.dexon-technology.com/${story.value.full_slug}`,
  }
}

// Individual ILI service pages (upstream/midstream/downstream/tailings-in-line-inspection, and
// others in the same family) share the generic "page" component like case studies do — the
// distinguishing signal here is that their first body block is "main-hero-2", combined with living
// under pipeline-services/ so this doesn't false-positive on an unrelated page that happens to
// reuse the same hero block elsewhere on the site.
function pipelineServiceSchema() {
  const content = story.value?.content
  if (!content)
    return null

  const usesMainHero2 = content.body?.[0]?.component === 'main-hero-2'
  const isPipelineService = story.value?.full_slug?.startsWith('pipeline-services/')
  if (!usesMainHero2 || !isPipelineService)
    return null

  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    'name': content.title,
    'serviceType': 'In-line Inspection (ILI)',
    'description': content.description,
    'provider': {
      '@type': 'Organization',
      'name': 'Dexon Technology',
    },
    'areaServed': 'Worldwide',
    'url': `https://www.dexon-technology.com/${story.value.full_slug}`,
  }
}

// Industry-specific inspection pages (oil-and-gas, marine, manufacturing, etc.) use "sub-hero-articles"
// as their first body block — but that block is reused all over the site for unrelated pages (Resources,
// Advanced NDT Services, ...), so it can't be the detection signal here. The full_slug prefix is the
// only reliable, narrow match for "this specific family of pages".
const INDUSTRY_LABELS = {
  'oil-and-gas': 'Oil & Gas',
  'marine': 'Marine',
  'manufacturing': 'Manufacturing',
  'renew-energy': 'Renewable Energy',
  'factories-logistics': 'Factories & Logistics',
  'individual-certificate': 'Certified Personnel',
  'transportation': 'Transportation',
}

function industryInspectionSchema() {
  const content = story.value?.content
  const fullSlug = story.value?.full_slug
  if (!content || !fullSlug?.startsWith('inspection-services/industries/'))
    return null

  const industryLabel = INDUSTRY_LABELS[fullSlug.split('/')[2]]

  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    'name': content.title,
    'serviceType': 'Industrial Inspection Services',
    'description': content.description,
    'provider': {
      '@type': 'Organization',
      'name': 'Dexon Technology',
    },
    'areaServed': 'Worldwide',
    ...(industryLabel ? { 'audience': { '@type': 'Audience', 'audienceType': industryLabel } } : {}),
    'url': `https://www.dexon-technology.com/${fullSlug}`,
  }
}

// Every other service page: the rest of inspection-services/* (industries/* has its own schema above)
// and any pipeline-services/* page pipelineServiceSchema() didn't claim (it needs a main-hero-2 first block).
const SERVICE_TYPES = {
  'inspection-services': 'Non-destructive testing and inspection',
  'pipeline-services': 'Pipeline in-line inspection',
}

function genericServiceSchema() {
  const content = story.value?.content
  const segments = pageSlug.split('/')
  if (!content || segments.length < 2 || !SERVICE_TYPES[segments[0]])
    return null

  const node = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    'name': content.title?.split('|')[0].trim(),
    'serviceType': SERVICE_TYPES[segments[0]],
    'description': content.description,
    'provider': {
      '@type': 'Organization',
      '@id': 'https://www.dexon-technology.com/#organization',
      'name': 'Dexon Technology',
    },
    'areaServed': 'Worldwide',
    'url': `https://www.dexon-technology.com/${pageSlug}`,
  }

  // Optional Storyblok fields — not created yet; picked up automatically once populated.
  // `offerings`: blocks of { name, description }. `stats`: blocks of { label, value }.
  if (Array.isArray(content.offerings) && content.offerings.length) {
    node.hasOfferCatalog = {
      '@type': 'OfferCatalog',
      'name': `${node.name} — services`,
      'itemListElement': content.offerings.map(o => ({
        '@type': 'Offer',
        'itemOffered': { '@type': 'Service', 'name': o.name, ...(o.description ? { description: o.description } : {}) },
      })),
    }
  }
  if (Array.isArray(content.stats) && content.stats.length) {
    node.additionalProperty = content.stats.map(s => ({ '@type': 'PropertyValue', 'name': s.label, 'value': s.value }))
  }

  return node
}

// Pages whose type is more specific than a plain WebPage.
const PAGE_TYPES = {
  'aboutus': 'AboutPage',
  'contact-us': 'ContactPage',
  'learn-more': 'CollectionPage',
  'resources': 'CollectionPage',
}

function pageTypeSchema() {
  const content = story.value?.content
  const type = PAGE_TYPES[pageSlug]
  if (!content || !type)
    return null

  return {
    '@context': 'https://schema.org',
    '@type': type,
    'name': content.title?.split('|')[0].trim(),
    'description': content.description,
    'url': `https://www.dexon-technology.com/${pageSlug}`,
    'isPartOf': { '@id': 'https://www.dexon-technology.com/#website' },
    'inLanguage': 'en',
  }
}

const schema = articleSchema() || jobPostingSchema() || pipelineServiceSchema() || industryInspectionSchema() || genericServiceSchema() || pageTypeSchema()

useHead({
  title: story.value?.content.title,
  meta: [
    {
      name: 'description',
      content: story.value?.content.description,
    },
    {
      name: 'keywords',
      content: story.value?.content.keyword ? story.value?.content.keyword : '',
    },
    {
      property: 'og:title',
      content: story.value?.content.title,
    },
    {
      property: 'og:type',
      content: 'website',
    },
    {
      property: 'og:locale',
      content: locale.value === 'th' ? 'th_TH' : 'en_US',
    },
    {
      property: 'og:description',
      content: story.value?.content.description,
    },
    {
      property: 'og:image',
      content: story.value?.content?.thumbnail?.filename ? story.value?.content?.thumbnail?.filename : '',
    },
    {
      property: 'og:image:width',
      content: '200',
    },
    {
      property: 'og:image:height',
      content: '200',
    },
    {
      property: 'og:url',
      content: `https://www.dexon-technology.com/${story.value?.full_slug}`,
    },
    {
      name: 'twitter:card',
      content: story.value?.content?.thumbnail?.filename ? story.value?.content?.thumbnail?.filename : '',
    },
    {
      name: 'twitter:title',
      content: story.value?.content.title,
    },
    {
      name: 'twitter:description',
      content: story.value?.content.description,
    },
    {
      name: 'twitter:image',
      content: story.value?.content?.thumbnail?.filename ? story.value?.content?.thumbnail?.filename : '',
    },
  ],
  script: schema ? [{ type: 'application/ld+json', innerHTML: JSON.stringify(schema) }] : [],
})

useBreadcrumbSchema(story.value?.content?.title)
</script>

<template>
  <div v-if="path === `/news/${story?.slug}` || path === `/th/news/${story?.slug}`" class="pt-mobile-navbar lg:pt-desktop-navbar">
    <div class="pb-mobile pt-custom-sm container lg:pb-desktop">
      <div class="mx-auto max-w-4xl">
        <LayoutsBreadcrump class="mb-custom-sm pl-0 pt-custom-lg !flex !px-0">
          <template #breadcrumbs>
            <div class="flex items-center gap-x-3 text-sm">
              <NuxtLink
                class="hover:text-secondary-red-100"
                :to="localePath({
                  name: 'news',
                })"
              >
                News
              </NuxtLink>
              <span class="flex">
                <IconBreadcrump class="w-[9px] text-primary" />
              </span>
            </div>
            <div class="items-center gap-x-3 text-sm md:flex">
              <span class="capitalize">
                {{ story?.content?.title }}
              </span>
            </div>
          </template>
        </LayoutsBreadcrump>
        <StoryblokComponent v-if="story?.content" :blok="story.content" :date="story?.created_at" />
        <div v-else class="container">
          <UISpinner class="m-auto min-h-[350px] lg:min-h-[700px]" />
        </div>
      </div>
    </div>
  </div>
  <div v-else-if="path === `/career-at-dexon/${story?.slug}` || path === `/th/career-at-dexon/${story?.slug}`" class="pt-mobile-navbar lg:pt-desktop-navbar">
    <div>
      <div class="relative">
        <LayoutsBreadcrump class="pt-6">
          <template #breadcrumbs>
            <div class="flex items-center gap-x-3 text-sm text-white">
              <span class="flex md:hidden">
                <svg class="text-white -scale-100" width="20" height="20" viewBox="0 0 24 24"><path fill="currentColor" d="M10 6L8.59 7.41L13.17 12l-4.58 4.59L10 18l6-6l-6-6z" /></svg>
              </span>
              <NuxtLink
                class="hover:text-secondary-red-100"
                :to="localePath({
                  name: 'career-at-dexon',
                })"
              >
                {{ $t('career.page.jobs') }}
              </NuxtLink>
              <span class="hidden md:flex">
                <IconBreadcrump class="w-[9px] text-white" />
              </span>
            </div>
            <div class="hidden items-center gap-x-3 text-sm text-white md:flex">
              <span class="hidden text-white md:flex">
                {{ story?.content?.title }}
              </span>
            </div>
          </template>
        </LayoutsBreadcrump>
        <div class="pb-20 pt-12 lg:pb-28 lg:pt-20 md:pb-24 md:pt-16">
          <HeroBlock :image-url="`${story?.content?.image?.filename}/m/1600x0`" :description="story?.content?.detail">
            <template #header>
              <span class="font-medium">{{ $t('career.page.header') }}</span>
            </template>
            <template #title>
              <span class="font-semibold capitalize">{{ story?.content?.title }}</span>
            </template>
          </HeroBlock>
        </div>
      </div>
      <div class="main-section flex flex-col pb-custom-lg pt-custom-lg container lg:block lg:pb-custom-xl lg:pt-custom-xl">
        <div class="left-side">
          <div class="mb-custom-lg lg:mb-custom-xl lg:pr-custom-xl">
            <StoryblokComponent v-if="story?.content" :blok="story.content" :date="story?.created_at" />
            <div v-else>
              <UISpinner class="m-auto min-h-[350px] lg:min-h-[700px]" />
            </div>
          </div>
        </div>
        <div class="right-side mb-custom-lg lg:mb-0">
          <CardSideBarCard :story="story?.content" @action="scrollToElement">
            <!-- <template #location>
              <span v-if="formattedJobLocation.length" class="text-primary/70 font-medium">{{ filteredJobLocation() }}</span>
            </template> -->
            <template #time>
              <span v-if="formattedJobTime.length" class="text-primary/70 font-medium">{{ filteredJobTime() }}</span>
            </template>
            <template #categories>
              <span v-if="formattedJobCategories.length" class="text-primary/70 font-medium capitalize">{{ filteredJobCategories() }}</span>
            </template>
          </CardSideBarCard>
        </div>
      </div>
    </div>
    <div class="bg-slate-100">
      <div ref="target" class="pb-mobile pt-14 container lg:pb-desktop lg:pt-20">
        <div class="lg:w-[65%] xl:w-[70%]">
          <div
            class="flex items-center text-md text-secondary-red-100 font-semibold leading-[2] capitalize"
          >
            {{ $t('career.form.apply') }}
          </div>
          <h3 class="mb-custom-sm text-xl font-bold lg:text-3xl md:text-2xl">
            {{ $t('career.form.title') }}
          </h3>
          <FormApply :multiple-mail="multipleMail" :questions-deatil="story?.content?.questions ? story?.content?.questions : []" :questions-card="story?.content?.components ? story?.content?.components : []" :position="story?.content?.title" :location="filteredJobLocation()" />
        </div>
      </div>
    </div>
  </div>
  <div v-else-if="path === `/submit-cv` || path === `/th/submit-cv`" class="pt-mobile-navbar lg:pt-desktop-navbar">
    <div class="bg-slate-100">
      <div ref="target" class="pb-mobile pt-14 container lg:pb-desktop lg:pt-20">
        <div class="lg:w-[65%] xl:w-[70%]">
          <div
            class="flex items-center text-md text-secondary-red-100 font-semibold leading-[2] capitalize"
          >
            {{ $t('career.form.apply') }}
          </div>
          <h3 class="mb-custom-sm text-xl font-bold lg:text-3xl md:text-2xl">
            {{ $t('career.form.title') }}
          </h3>
          <FormApply :available="story?.content?.body?.[0]?.options" :questions-deatil="story?.content?.questions ? story?.content?.questions : []" :questions-card="story?.content?.components ? story?.content?.components : []" :position="story?.content?.title" :location="filteredJobLocation()" />
        </div>
      </div>
    </div>
  </div>
  <div v-else>
    <StoryblokComponent v-if="story?.content" :blok="story.content" />
  </div>
</template>

<style scoped>
.main-section:before, .main-section:after {
  content:'';
  display:table;
  clear:both;
}

.right-side {
  @apply lg:float-left sticky inline-block lg:w-[350px] lg:top-[60px] top-mobile-navbar -order-1;
}

.left-side {
  @apply lg:float-left lg:align-top inline-block lg:w-[calc(100%-350px)];
}
</style>
